// lib/pricing/externalPricing.ts
// Browser-side fetch + parse module for per-customer pricing endpoints.
//
// Used for B2B clients (e.g. acalight) where each logged-in customer sees
// negotiated prices from the host site's own pricing API. The call is made
// from the user's browser to the host's same-origin endpoint, so the session
// cookie identifies the customer automatically — no auth handling on our side.
//
// This module runs in the browser only.

import type { ExternalPricingConfig } from '@/lib/clients/types';
import { enqueueHostRequest, hostKeyForUrl } from './hostQueue';

/**
 * Default ids per pricing request.
 *
 * The endpoint is a GET with comma-separated ids in the URL. A host's SEO
 * redirect can re-encode each comma to %2C (3 bytes), so the FOLLOWED request
 * hits the typical 8 KB request-line limit early — measured on acalight's
 * staging: 1,000 ids OK, 1,300 silently truncated to ~1,040, 1,500 → HTTP 414.
 * 800 leaves comfortable margin even for 6-digit ids.
 *
 * The real ceiling is per-host and is about per-id COMPUTATION COST, not URL
 * length (a logged-in call runs tax + B2B rules per id), so it is invisible to
 * unauthenticated probing — see `externalPricing.chunkSize` in the client
 * config, which pins the measured value per client (syfak: 500).
 */
export const PRICE_CHUNK_SIZE = 800;

/** Split ids into request-sized chunks. */
export function chunkIds(
  ids: Array<string | number>,
  size: number = PRICE_CHUNK_SIZE,
): Array<Array<string | number>> {
  const out: Array<Array<string | number>> = [];
  for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size));
  return out;
}

/**
 * Normalized per-product price returned by the host pricing endpoint.
 * Both fields are formatted strings (e.g. "12,50€"), or null when unavailable.
 */
export interface NormalizedPrice {
  /** Customer-specific regular price as a formatted string, or null if not available
   *  (guest user, no agreement, product not found). UI should fall back to RRP. */
  regular: string | null;
  /** Customer-specific sale price, or null if no active sale. */
  sale: string | null;
  /**
   * syfak month-offer URL. Non-empty → the product participates in an offer, so
   * the card shows "ΕΠΙΛΕΞΕ ΠΡΟΣΦΟΡΑ" (opens the offer popup that fetches this
   * URL) instead of a plain add-to-cart. Only populated by syfak_v1. Rides the
   * in-memory display fetch; NOT persisted in the price-map sessionStorage cache
   * (filtering needs regular/sale only).
   */
  offerUrl?: string | null;
  /**
   * syfak live per-customer status labels — fridge / prescription / near-expiry
   * (+ expiry date) / narcotic / discontinued / free-text message, etc. Keyed as
   * the endpoint returns them: '1'/'0' flags, or a date/text value. The card maps
   * them to Greek tags via ui.pricingLabels. Only populated by syfak_v1; rides
   * the in-memory display fetch, NOT the price-map sessionStorage cache.
   */
  labels?: Record<string, string> | null;
  /**
   * syfak live per-customer stock label (`stock_text`), e.g. «ΜΗΔΕΝΙΚΟ
   * ΑΠΟΘΕΜΑ». Three states, deliberately distinct:
   *   - a string   → the label the host's own site shows this customer
   *   - null       → the host answered "no label" (normal stock)
   *   - undefined  → the response carried no stock_text at all, so we know
   *                  nothing live (the card falls back to the indexed value)
   * Only populated by syfak_v1; in-memory only, never in the price-map cache.
   */
  stockText?: string | null;
  /**
   * syfak: how many of this product are ALREADY in the customer's flow basket
   * (ΤΙΜΗ ΡΟΗΣ), from the response's `quantity` (added by syfak 2026-10-01;
   * a STRING when > 0, e.g. "3", and the NUMBER 0 otherwise). Offer-basket
   * items report 0 by design — that basket adds up, the flow basket does not.
   * undefined = the response carried no usable quantity, so the card behaves
   * exactly as before (box starts at 1). See lib/basketQuantity.ts.
   */
  cartQuantity?: number;
  /**
   * The HOST's own product code for this entry, when the response reveals it.
   * This is an INTEGRITY SIGNAL, not display data: our index and the host key
   * on the same numeric product id, and if those id spaces ever drift apart the
   * widget would attach a real price to the WRONG product — silently, and with
   * money attached. This code lets `resolveCustomerPrice` verify that the host
   * answered about the product we asked about (compared against our `model`).
   *
   * Only syfak_v1 populates it, and only for products carrying an `offer_url`
   * (the code rides that URL's query string — see extractProductCode). Products
   * without an offer return no code, so they cannot be verified; drift is
   * systemic rather than per-product, so the covered subset works as the alarm.
   * Observed real drift 2026-07-27: staging id 39481 = our model 108794 but the
   * host's 108847 — a different product entirely (staging runs an older DB
   * snapshot than the live feed our index is built from).
   */
  productCode?: string | null;
}

/**
 * Pull the host's product code out of a syfak offer URL.
 *
 * Real shape (verbatim from their endpoint):
 *   index.php?route=extension/api4u/post_order/getOffers&product_code=01267&product_id=1122
 *
 * Parsed with a plain regex rather than URL/URLSearchParams because the value
 * is a RELATIVE url — `new URL()` would throw without a base, and the base
 * differs per host. Returns null when absent/empty so the guard stays inert.
 */
export function extractProductCode(offerUrl: string | null | undefined): string | null {
  if (typeof offerUrl !== 'string' || !offerUrl) return null;
  const match = /[?&]product_code=([^&]*)/.exec(offerUrl);
  if (!match) return null;
  const code = decodeURIComponent(match[1]).trim();
  return code || null;
}

/**
 * Parse a formatted customer-price string ("12,50€", "1.234,56€") to a number.
 * Returns null when the input is null/empty/unparseable.
 *
 * Used for client-side price-range filtering on external-pricing clients:
 * the pricing endpoint returns formatted strings, but the sidebar's price-band
 * filter and any query-extracted ("μέχρι 10€") range need numeric comparison.
 *
 * Accepts both Greek (`12,50€`) and English (`$12.50`) decimal conventions.
 */
export function parsePriceString(s: string | null | undefined): number | null {
  if (!s) return null;
  // Strip currency, whitespace, and the EN thousands separator. Then convert
  // the remaining decimal comma to a dot. This handles "1.234,56" (EL) and
  // "1,234.56" (EN) — in the EL form the `.` is thousands so we strip it,
  // in the EN form the comma is thousands so we strip it. Then normalize.
  const cleaned = s.trim().replace(/[€$£\s]/g, '');
  // Heuristic: if there's both `.` and `,`, the LAST one is the decimal
  // separator. Everything before the last separator that's the OTHER symbol
  // is a thousands separator and should be removed.
  let normalized = cleaned;
  const lastDot = cleaned.lastIndexOf('.');
  const lastComma = cleaned.lastIndexOf(',');
  if (lastDot >= 0 && lastComma >= 0) {
    if (lastComma > lastDot) {
      normalized = cleaned.replace(/\./g, '').replace(',', '.');
    } else {
      normalized = cleaned.replace(/,/g, '');
    }
  } else if (lastComma >= 0) {
    normalized = cleaned.replace(',', '.');
  }
  const num = parseFloat(normalized);
  return isNaN(num) ? null : num;
}

/**
 * Resolve a customer's effective numeric price from a NormalizedPrice entry.
 * Sale price wins if present; otherwise regular. Returns null for [false, false]
 * (no agreement / guest) or unparseable inputs.
 */
export function effectiveCustomerPrice(entry: NormalizedPrice | null | undefined): number | null {
  if (!entry) return null;
  return parsePriceString(entry.sale) ?? parsePriceString(entry.regular);
}

/**
 * Fetch customer-specific prices for a batch of product IDs.
 *
 * Returns a Map keyed by product_id (as string) → NormalizedPrice.
 * Throws on transport/timeout/parse errors — the UI should catch and fall back
 * to RRP gracefully.
 */
export async function fetchCustomerPrices(
  productIds: Array<string | number>,
  config: ExternalPricingConfig,
  opts: { timeoutMs?: number; signal?: AbortSignal } = {},
): Promise<Map<string, NormalizedPrice>> {
  if (productIds.length === 0) return new Map();

  const idString = productIds.join(config.idSeparator);
  const url = buildRequestUrl(config, idString);

  // SERIALISED per host (2026-07-27). These hosts refuse a second request that
  // arrives while the first is still computing (syfak: HTTP 503), so every call
  // waits its turn — see lib/pricing/hostQueue.ts for the full reasoning.
  return enqueueHostRequest(hostKeyForUrl(url), async () => {
    // Checked INSIDE the queued task, not before enqueuing: a search that was
    // superseded while waiting in line must not spend the host's one slot.
    if (opts.signal?.aborted) throw new DOMException('Aborted', 'AbortError');

    const controller = new AbortController();
    // config.timeoutMs is tuned for the interactive per-search fetch; background
    // work (the price-map build) passes a larger budget — the host computes
    // taxes/B2B rules per id, and a logged-in batch can legitimately take >3s.
    // NB the timeout starts when the request STARTS, not when it was queued.
    const timeoutId = setTimeout(() => controller.abort(), opts.timeoutMs ?? config.timeoutMs);
    // Caller-supplied signal (a superseded search) also aborts the live request,
    // so it stops occupying the host instead of running on to completion.
    const onExternalAbort = () => controller.abort();
    opts.signal?.addEventListener('abort', onExternalAbort);

    try {
      const response = await fetch(url, {
        method: config.requestMethod,
        credentials: config.credentialsMode,
        signal: controller.signal,
        ...(config.requestMethod === 'POST'
          ? {
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ [config.idParam]: idString }),
            }
          : {}),
      });

      if (!response.ok) {
        throw new Error(`Pricing endpoint returned ${response.status}`);
      }

      const json: unknown = await response.json();
      return parseResponse(json, config.responseFormat);
    } finally {
      clearTimeout(timeoutId);
      opts.signal?.removeEventListener('abort', onExternalAbort);
    }
  });
}

/** Build the request URL. For GET, append ids as a query param. POST sends body. */
function buildRequestUrl(config: ExternalPricingConfig, idString: string): string {
  if (config.requestMethod === 'POST') return config.endpointUrl;
  const separator = config.endpointUrl.includes('?') ? '&' : '?';
  return `${config.endpointUrl}${separator}${config.idParam}=${encodeURIComponent(idString)}`;
}

/** Dispatch by response format. Add new formats here as new clients arrive. */
function parseResponse(json: unknown, format: string): Map<string, NormalizedPrice> {
  switch (format) {
    case 'opencart_v1':
      return parseOpencartV1(json);
    case 'syfak_v1':
      return parseSyfakV1(json);
    default:
      throw new Error(`Unknown pricing response format: ${format}`);
  }
}

/**
 * Parse OpenCart's `ajax_get_prices` response shape:
 *   { "40493": ["12,50€", "9,90€"], "12345": ["25,00€", false] }
 *
 * - First element = regular price (string) or `false`
 * - Second element = sale price (string) or `false` (no sale)
 * - `[false, false]` = product not found OR guest user without permission OR
 *   logged-in customer with no agreement. We treat all three as "no price available".
 */
function parseOpencartV1(json: unknown): Map<string, NormalizedPrice> {
  const out = new Map<string, NormalizedPrice>();
  if (!json || typeof json !== 'object') return out;

  for (const [id, value] of Object.entries(json as Record<string, unknown>)) {
    if (!Array.isArray(value) || value.length < 2) continue;
    const [rawRegular, rawSale] = value;
    out.set(String(id), {
      regular: typeof rawRegular === 'string' ? rawRegular : null,
      sale: typeof rawSale === 'string' ? rawSale : null,
    });
  }
  return out;
}

/**
 * Parse syfak's `ajax_get_prices` response.
 *
 * Unlike acalight's array-only shape, syfak returns a HYBRID (confirmed via a
 * live staging test, 2026-07-22, products 1121/1122):
 *   - No price for this customer → the SAME `[false, false]` array as opencart
 *     (guest / no agreement / product not found).
 *   - A price exists             → a NAMED-FIELD OBJECT:
 *       { price: "21,15€", special: "", offer_url: "...", labels: {...}, stock_text: "..." }
 *     • price   = regular price (formatted string; "" when absent)
 *     • special = flow-discount price (formatted string; "" = no discount)
 *
 * We map price → regular and special → sale, so effectiveCustomerPrice (sale
 * wins over regular) and the "hide no-price products under an active price
 * filter" rule behave exactly as for acalight. We ALSO surface offer_url →
 * offerUrl: a non-empty value means the product is in a month-offer, so the
 * card shows "ΕΠΙΛΕΞΕ ΠΡΟΣΦΟΡΑ" (opens the offer popup) instead of a plain
 * add-to-cart. offerUrl rides the in-memory display fetch only — priceMap.ts
 * intentionally does NOT persist it to the sessionStorage cache (filtering
 * needs regular/sale only). labels (live status flags) feed the card's status
 * tags; stock_text → stockText feeds the availability badge (lib/productBadge).
 * Empty → null.
 */
/**
 * syfak `stock_text` → NormalizedPrice.stockText. Key ABSENT → undefined (we
 * learned nothing live; the card keeps the indexed value). Present but null or
 * blank → null: the host's answer is "no label", which on 2026-10-01 is what it
 * sends for normal stock (feed: empty stock_status). Whitespace is collapsed so
 * a stray double space cannot miss the colour map.
 */
function parseStockText(o: Record<string, unknown>): string | null | undefined {
  if (!('stock_text' in o)) return undefined;
  const v = o.stock_text;
  if (typeof v !== 'string') return null;
  const s = v.replace(/\s+/g, ' ').trim();
  return s || null;
}

/**
 * syfak `quantity` → NormalizedPrice.cartQuantity. Accepts the number or the
 * numeric string syfak sends; anything else (absent, blank, negative,
 * fractional, garbage) → undefined, i.e. "unknown", never a guess.
 */
function parseCartQuantity(v: unknown): number | undefined {
  const n = typeof v === 'number' ? v : typeof v === 'string' && /^\s*\d+\s*$/.test(v) ? Number(v) : NaN;
  return Number.isInteger(n) && n >= 0 ? n : undefined;
}

export function parseSyfakV1(json: unknown): Map<string, NormalizedPrice> {
  const out = new Map<string, NormalizedPrice>();
  if (!json || typeof json !== 'object') return out;

  for (const [id, value] of Object.entries(json as Record<string, unknown>)) {
    if (Array.isArray(value)) {
      // No-price / opencart-style entry: [regular, sale]; [false, false] = none.
      const [rawRegular, rawSale] = value;
      out.set(String(id), {
        regular: typeof rawRegular === 'string' && rawRegular.trim() ? rawRegular : null,
        sale: typeof rawSale === 'string' && rawSale.trim() ? rawSale : null,
      });
    } else if (value && typeof value === 'object') {
      // Priced entry: named fields. Empty-string price/special → null.
      // NOTE offer_url is not always a string: the live endpoint returns the
      // BOOLEAN `false` for non-offer products (confirmed 2026-07-27 against a
      // real commercial-policy account). The `typeof === 'string'` guard below
      // is what makes that safe — do not relax it to a truthiness check.
      const o = value as Record<string, unknown>;
      const offerUrl = typeof o.offer_url === 'string' && o.offer_url.trim() ? o.offer_url : null;
      out.set(String(id), {
        regular: typeof o.price === 'string' && o.price.trim() ? o.price : null,
        sale: typeof o.special === 'string' && o.special.trim() ? o.special : null,
        offerUrl,
        labels: o.labels && typeof o.labels === 'object' ? (o.labels as Record<string, string>) : null,
        productCode: extractProductCode(offerUrl),
        stockText: parseStockText(o),
        cartQuantity: parseCartQuantity(o.quantity),
      });
    }
    // else: unexpected value → skip (id absent → resolves to "unavailable").
  }
  return out;
}
